/**
 * Document validity.
 *
 * Supporting documents go stale: a bank statement or a proof of address from a year ago says
 * little about a customer today. The business rule is a flat three months from the upload date,
 * applied to every document type.
 *
 * Validity is derived from the upload date rather than stored against the document, so nothing
 * has to be written to the document service and every document already on file is covered the
 * moment this ships. It is a presentation rule only: expired documents are flagged and warned
 * about, never blocked, so an officer can still proceed for a client who cannot immediately
 * re-supply a document.
 */

export const DOCUMENT_VALIDITY_MONTHS = 3;

/** Expiry is only worth drawing attention to as it approaches. */
export const DOCUMENT_EXPIRING_SOON_DAYS = 14;

export interface DocumentValidity {
  /** The day the document stops counting as current. */
  expiresAt: Date;
  isExpired: boolean;
  /** True while the document is still valid but close to expiring. */
  isExpiringSoon: boolean;
  /** Whole days until expiry; negative once expired. */
  daysRemaining: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Month arithmetic that does not roll over the month end: 31 January plus three months is
 * 30 April, not 1 May, which is what a plain setMonth would produce.
 */
const addMonthsClamped = (date: Date, months: number): Date => {
  const target = new Date(date);
  const dayOfMonth = target.getDate();

  target.setDate(1);
  target.setMonth(target.getMonth() + months);

  const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));

  return target;
};

/** Whole days between two dates, ignoring the time of day so a document keeps all of its last day. */
const wholeDaysBetween = (from: Date, to: Date): number => {
  const startOfDay = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((startOfDay(to) - startOfDay(from)) / MS_PER_DAY);
};

/** Returns undefined when there is no usable upload date, so callers can leave the cell blank. */
export const getDocumentValidity = (
  uploadDate?: string | null,
  now: Date = new Date()
): DocumentValidity | undefined => {
  if (!uploadDate) return undefined;

  const uploaded = new Date(uploadDate);
  if (Number.isNaN(uploaded.getTime())) return undefined;

  const expiresAt = addMonthsClamped(uploaded, DOCUMENT_VALIDITY_MONTHS);
  const daysRemaining = wholeDaysBetween(now, expiresAt);

  return {
    expiresAt,
    isExpired: daysRemaining < 0,
    isExpiringSoon: daysRemaining >= 0 && daysRemaining <= DOCUMENT_EXPIRING_SOON_DAYS,
    daysRemaining
  };
};

export const isDocumentExpired = (uploadDate?: string | null, now: Date = new Date()): boolean =>
  getDocumentValidity(uploadDate, now)?.isExpired ?? false;

/** Short wording for a badge: "Expired", "Expires in 3 days", "Valid". */
export const getDocumentValidityLabel = (validity: DocumentValidity): string => {
  if (validity.isExpired) return 'Expired';
  if (!validity.isExpiringSoon) return 'Valid';
  if (validity.daysRemaining === 0) return 'Expires today';
  return `Expires in ${validity.daysRemaining} day${validity.daysRemaining === 1 ? '' : 's'}`;
};
